"use client";

import React from "react";
import { ShieldCheck, Droplets, Maximize, Layers, Ruler } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const features = [{
  title: "Advanced Tanking Systems",
  description: "We utilize market-leading waterproof tanking membranes to create a completely impermeable barrier behind your tiles, ensuring zero leaks for decades.",
  icon: ShieldCheck,
  bgImage: "bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/waterproofing_membrane_technical.png)]",
  colSpan: "lg:col-span-4"
}, {
  title: "Epoxy Grout Finishing",
  description: "Unlike standard grout, our epoxy resin grout is impervious to water, stain-resistant, and incredibly durable, maintaining a pristine look without discoloration.",
  icon: Droplets,
  bgImage: "bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/epoxy_grout_detail.png)]",
  colSpan: "lg:col-span-4"
}, {
  title: "Precision 45° Mitring",
  description: "Forget plastic trims. We grind tile edges to a perfect 45-degree angle for seamless, sharp external corners that deliver a monolithic, high-end stone aesthetic.",
  icon: Maximize,
  bgImage: "bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/mitred_tile_corner.png)]",
  colSpan: "lg:col-span-4"
}, {
  title: "Ditra Matting Installation",
  description: "We install decoupling membranes like Ditra to neutralize stresses between the substrate and the tile covering, preventing cracks caused by structural movement.",
  icon: Layers,
  bgImage: "bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/ditra_decoupling_mat.png)]",
  colSpan: "lg:col-span-8"
}, {
  title: "Laser-Levelled Substrates",
  description: "A perfect finish starts beneath the surface. We use premium self-levelling compounds to ensure a perfectly flat base, critical for large-format porcelain installations.",
  icon: Ruler,
  bgImage: "bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/laser_level_construction.png)]",
  colSpan: "lg:col-span-4"
}];
export default function TheWaterproofingEdge() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="thewaterproofingedge" ref={ref} className="bg-background py-[120px] overflow-hidden text-foreground">
      <div className="container px-6">
        <div className={cn("mb-20 max-w-4xl transition-all duration-1000 ease-out transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <div className="inline-block px-4 py-1.5 mb-8 text-xs font-bold tracking-[0.2em] uppercase bg-secondary/20 text-secondary border border-secondary/30 rounded-[2px] backdrop-blur-sm">
            Technical Excellence
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-7xl font-bold text-foreground tracking-tight mb-8">
            The Waterproofing Edge
          </h2>
          <p className="text-lg md:text-xl text-foreground/70 max-w-2xl leading-relaxed font-light">
            At SimProTiling, we believe what happens behind the tile is just as important as the finish itself. 
            Our technical approach combines precision engineering with the finest materials to guarantee longevity.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          const delayClass = i === 0 ? "delay-100" : i === 1 ? "delay-200" : i === 2 ? "delay-300" : i === 3 ? "delay-400" : "delay-500";
          return <div key={i} data-index={i} className={cn(feature.colSpan, "transition-all duration-1000 ease-out transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12", delayClass)}>
                <Card className={cn("relative overflow-hidden h-full border-white/10 rounded-[2px] bg-black/20 group min-h-[450px] flex flex-col justify-end transition-all duration-500 hover:border-secondary/50 py-0 gap-0", feature.bgImage, "bg-cover bg-center")}>
                  <div className={cn("absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent transition-opacity duration-500 group-hover:opacity-80", i === 0 && "text-white")} />
                  
                  <CardHeader className="relative z-10 p-8 pb-4 space-y-4">
                    <div className="inline-flex items-center justify-start px-3 w-12 h-12 rounded-[2px] bg-secondary/20 text-secondary border border-secondary/30 backdrop-blur-sm transition-transform duration-500 group-hover:scale-110">
                      <Icon strokeWidth={1} className="w-6 h-6" />
                    </div>
                    <CardTitle className="font-serif text-2xl md:text-3xl text-card-foreground font-bold tracking-tight">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="relative z-10 px-8 pb-10">
                    <CardDescription className="text-base text-card-foreground/70 leading-relaxed font-light">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}